import { FC, useEffect, useState } from 'react';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentDetails } from './Form/PaymentDetails';
import theme from 'config/theme';
import {
  NavigationAmountDetails,
  NavigationContainer,
  OlNavigation,
  ProgressBar,
} from 'screens/Checkout/styled';
import {
  formatNumberAsCurrency,
  getPathWithRef,
  getUrlSearchParamsStr,
  noop,
  isObjectEmpty,
} from 'helpers/utils';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { IUserData } from 'store/user/types';
import {
  canSkipPersonalDetails,
  getInitialValuesFromApiDataStripe,
  getIsEmployerAddressRequiredByCandidates,
  getPayRequestBodyStripe,
  getProgressBarWidthStripe,
  getUpdatedUser,
  InitialValuesInterface,
} from 'screens/Checkout/Form/utils';
import { Link } from 'components/Link';
import { Wizard } from 'screens/Checkout/Form/Wizard';
import { Details } from 'screens/Checkout/Form/Details';
import { Review } from 'screens/Checkout/Form/Review';
import { postPaymentIntentStripeAPI, storeStripePaymentIntent } from 'store/pay/actions';
import {
  clearCheckoutIdempotencyKey,
  getCheckoutIdempotencyKey,
  setCheckoutIdempotencyKey,
} from 'services/storage';
import { v4 as uuidv4 } from 'uuid';
import { postUserStripeCustomer, updateUserProfile } from 'store/user/actions';
import { useAppDispatch } from 'helpers/hooks';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { getUrlSearchParamsStrWithoutRecipients } from 'helpers/donate';
import { ENV_CONFIG } from 'config/environment';
import { FORM_ERROR } from 'final-form';

const formPages = [
  {
    navigationTitle: '2. Personal Details',
  },
  {
    navigationTitle: '3. Payment & Confirm',
  },
];

interface Props {
  candidates: CandidateType[];
  clientId?: string;
  donationAmount: number;
  pageId?: number;
  setDonationAmountWithTip: (amount: number) => void;
  user?: IUserData;
}

export const CheckoutForm: FC<Props> = ({
  candidates,
  clientId,
  donationAmount,
  pageId,
  setDonationAmountWithTip,
  user,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [initialValues, setInitialValues] = useState({});
  const [prevSubmittedValuesStr, setPrevSubmittedValuesStr] = useState('');

  useEffect(() => {
    if (user?.email && donationAmount) {
      setInitialValues(getInitialValuesFromApiDataStripe(donationAmount, user));
    }
  }, [donationAmount, user?.email]);

  useEffect(() => {
    if (!getCheckoutIdempotencyKey()) {
      setCheckoutIdempotencyKey(uuidv4());
    }
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(initialValues)) {
      setActivePageIndex(
        canSkipPersonalDetails(initialValues as InitialValuesInterface, candidates) ? 1 : 0
      );
    }
  }, [initialValues]);

  const onTipChange = (tip: number) => {
    setDonationAmountWithTip(donationAmount + tip);
  };

  const onPersonalDetailsComplete = (values: any) => {
    // create stripe customer if needed
    try {
      if (user && !user.stripe_customer_id) {
        dispatch(
          postUserStripeCustomer({
            name: `${values.first_name} ${values.last_name}`,
            email: user.email,
          })
        );
      }
    } catch {
      noop(); // allow for checkout if this fails
    }
    // update user with form values
    try {
      if (user) {
        const updatedUser = getUpdatedUser(user, values);
        if (!isObjectEmpty(updatedUser)) {
          dispatch(updateUserProfile(updatedUser));
        }
      }
    } catch {
      noop(); // allow for checkout if this fails
    }
  };

  const onNext = (currentPageIndex: number, values: any) => {
    if (currentPageIndex === 0) {
      onPersonalDetailsComplete(values);
    }
  };

  const onSubmit = async formValues => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    // Trigger form validation and wallet collection
    // We can get selectedPaymentMethod here and add to the request body for cc vs ach etc
    const { error: submitError, selectedPaymentMethod } = await elements.submit();
    if (submitError) {
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: submitError.message,
        });
      });
    }

    const requestBody = getPayRequestBodyStripe({
      formValues,
      candidates,
      clientId,
      pageId,
      selectedPaymentMethod,
    });
    const requestBodyStr = JSON.stringify(requestBody);

    // if there is no idempotency key or the data changed from the last request, create one
    if (
      !getCheckoutIdempotencyKey() ||
      (prevSubmittedValuesStr && requestBodyStr !== prevSubmittedValuesStr)
    ) {
      setCheckoutIdempotencyKey(uuidv4());
    }

    const idempotencyKey = getCheckoutIdempotencyKey();
    if (!idempotencyKey) {
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: 'No idempotency key found. Cannot complete payment.',
        });
      });
    }

    try {
      // Create the PaymentIntent and obtain clientSecret
      const response = await postPaymentIntentStripeAPI(requestBody, idempotencyKey, !!user);
      const { client_secret: clientSecret } = response.data;
      setPrevSubmittedValuesStr(requestBodyStr);

      // Confirm the PaymentIntent using the details collected by the Payment Element
      const stripeResponse = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${
            ENV_CONFIG().SITE_URL
          }/donate/confirmation/standard?${getUrlSearchParamsStrWithoutRecipients()}`,
        },
        redirect: 'if_required',
      });

      if (stripeResponse.error) {
        clearCheckoutIdempotencyKey();
        return new Promise(resolve => {
          resolve({
            [FORM_ERROR]: stripeResponse.error.message,
          });
        });
      }
      if (stripeResponse.paymentIntent) {
        dispatch(storeStripePaymentIntent(stripeResponse.paymentIntent));
        navigate(`/donate/confirmation/standard?${getUrlSearchParamsStrWithoutRecipients()}`);
      }
    } catch (error) {
      return new Promise(resolve => {
        resolve({
          [FORM_ERROR]: 'Error creating payment intent.',
        });
      });
    }
  };

  const backToDonatePath = getPathWithRef(`/donate?${getUrlSearchParamsStr()}`);

  return (
    <>
      <div className="row">
        <NavigationContainer $justifyContent="space-between">
          <OlNavigation>
            <li>
              <Link to={backToDonatePath}>
                <span className="body-small-text semi-bold color-inkBlue">1. Amount</span>
                <NavigationAmountDetails $fontType="semiBold" $color={theme.colors.inkBlue}>
                  {' ('}
                  {formatNumberAsCurrency(donationAmount)}
                  {')'}
                </NavigationAmountDetails>
              </Link>
            </li>
            {formPages.map(({ navigationTitle }, index) => (
              <li key={index}>
                <button
                  onClick={() => index < activePageIndex && setActivePageIndex(index)}
                  disabled={index >= activePageIndex}
                >
                  <span
                    className={classNames('body-small-text', {
                      'semi-bold': index <= activePageIndex,
                      'color-black shadow-7': index > activePageIndex,
                    })}
                  >
                    {navigationTitle}
                  </span>
                </button>
              </li>
            ))}
          </OlNavigation>
          <ProgressBar $width={getProgressBarWidthStripe(activePageIndex)} />
        </NavigationContainer>
      </div>
      <div>
        <span className="body-text color-black">Complete your </span>
        {activePageIndex === 2 ? (
          <span className="body-text color-black">contribution:</span>
        ) : (
          <span className="body-text color-black semi-bold">
            {formatNumberAsCurrency(donationAmount)} contribution:
          </span>
        )}
      </div>
      <Wizard
        initialValues={initialValues}
        backToDonatePath={backToDonatePath}
        onBackToDonatePage={noop}
        onNext={onNext}
        onSubmit={onSubmit}
        activePageIndex={activePageIndex}
        setActivePageIndex={setActivePageIndex}
        isSkipToReviewEnabled={false}
        onNavigateToLastPage={noop}
        validateArray={[() => ({}), () => ({}), () => ({})]}
      >
        <Details
          candidates={candidates}
          donationAmount={donationAmount}
          isEmployerAddressRequired={getIsEmployerAddressRequiredByCandidates(candidates)}
        />
        <Review
          candidates={candidates}
          donationAmount={donationAmount}
          numCandidates={candidates.length}
          backToDonatePath={backToDonatePath}
          onBackToDonatePage={noop}
          onTipChange={onTipChange}
          setActivePageIndex={setActivePageIndex}
          isCondensed={true}
          singleLine={true}
        >
          <PaymentDetails />
        </Review>
      </Wizard>
    </>
  );
};
